import type { ReactNode } from 'react';

import { STRUCTURAL_EDIT_LOCK_REASON } from './questionEditRestrictions';
import type { AddableQuestionPartType, EditableQuestion } from './editorTypes';
import { detectQuestionVariableNames } from './useContentEditor';
import { QuestionPartCard } from './QuestionPartCard';
import { TextUnitEditor } from './TextUnitEditor';
import { VariablesPanel } from './VariablesPanel';
import styles from './MultiPartEditor.module.css';

interface MultiPartEditorProps {
  afterStem?: ReactNode;
  disabled?: boolean;
  onAddPart: (type: AddableQuestionPartType) => void;
  onRemovePart: (partIndex: number) => void;
  onReorderPart: (fromIndex: number, toIndex: number) => void;
  onUpdatePart: (
    partIndex: number,
    updates: Partial<NonNullable<EditableQuestion['parts']>[number]>,
  ) => void;
  onUpdateQuestion: (updates: Partial<EditableQuestion>) => void;
  question: EditableQuestion;
  structuralEditsRestricted?: boolean;
}

export function MultiPartEditor({
  afterStem,
  disabled = false,
  onAddPart,
  onRemovePart,
  onReorderPart,
  onUpdatePart,
  onUpdateQuestion,
  question,
  structuralEditsRestricted = false,
}: MultiPartEditorProps) {
  const detectedVariableNames = detectQuestionVariableNames(question);
  const structuralDisabled = disabled || structuralEditsRestricted;

  return (
    <div className={styles.root}>
      <div className={styles.editorGroup}>
        <p className={styles.sectionLabel}>Shared Stem</p>
        <TextUnitEditor
          label="Shared stem"
          onChange={(contentHtml) => {
            onUpdateQuestion({ contentHtml });
          }}
          placeholder="Write what every part of the question shares…"
          value={question.contentHtml}
        />
      </div>

      {afterStem}

      <label className={styles.toggleField}>
        <input
          checked={question.useRandomVariables ?? false}
          disabled={structuralDisabled}
          onChange={(event) => {
            onUpdateQuestion({ useRandomVariables: event.target.checked });
          }}
          title={structuralEditsRestricted ? STRUCTURAL_EDIT_LOCK_REASON : undefined}
          type="checkbox"
        />
        <span>Use randomized variables</span>
      </label>

      {question.useRandomVariables ? (
        <VariablesPanel
          detectedVariableNames={detectedVariableNames}
          disabled={disabled}
          onAddVariable={() => {
            onUpdateQuestion({
              useRandomVariables: true,
              variables: [
                ...question.variables,
                {
                  id: `${question.id}-variable-${question.variables.length + 1}`,
                  name: `variable_${question.variables.length + 1}`,
                  type: 'NUMERICAL',
                },
              ],
            });
          }}
          onRemoveVariable={(variableIndex) => {
            onUpdateQuestion({
              variables: question.variables.filter(
                (_, currentIndex) => currentIndex !== variableIndex,
              ),
            });
          }}
          onUpdateVariable={(variableIndex, updates) => {
            onUpdateQuestion({
              variables: question.variables.map((variable, currentIndex) =>
                currentIndex === variableIndex
                  ? {
                      ...variable,
                      ...updates,
                    }
                  : variable,
              ),
            });
          }}
          structuralEditsRestricted={structuralEditsRestricted}
          variables={question.variables}
        />
      ) : null}

      <div className={styles.partsSection}>
        <div className={styles.partsHeader}>
          <h4 className={styles.partsTitle}>Parts</h4>
          <div className={styles.addActions}>
            <button
              className={styles.addButton}
              disabled={structuralDisabled}
              onClick={() => {
                onAddPart('MULTIPLE_CHOICE');
              }}
              title={structuralEditsRestricted ? STRUCTURAL_EDIT_LOCK_REASON : undefined}
              type="button"
            >
              Add MC Part
            </button>
            <button
              className={styles.addButton}
              disabled={structuralDisabled}
              onClick={() => {
                onAddPart('NUMERICAL_WITH_ERROR');
              }}
              title={structuralEditsRestricted ? STRUCTURAL_EDIT_LOCK_REASON : undefined}
              type="button"
            >
              Add Numerical Part
            </button>
            <button
              className={styles.addButton}
              disabled={structuralDisabled}
              onClick={() => {
                onAddPart('EXACT_ANSWER');
              }}
              title={structuralEditsRestricted ? STRUCTURAL_EDIT_LOCK_REASON : undefined}
              type="button"
            >
              Add Exact Part
            </button>
            <button
              className={styles.addButton}
              disabled={structuralDisabled}
              onClick={() => {
                onAddPart('AI_UPLOAD');
              }}
              title={structuralEditsRestricted ? STRUCTURAL_EDIT_LOCK_REASON : undefined}
              type="button"
            >
              Add AI Upload Part
            </button>
          </div>
        </div>

        <ol className={styles.partsList}>
          {(question.parts ?? []).map((part, partIndex, parts) => (
            <QuestionPartCard
              disabled={disabled}
              index={partIndex}
              key={part.id}
              onMoveDown={() => {
                onReorderPart(partIndex, partIndex + 1);
              }}
              onMoveUp={() => {
                onReorderPart(partIndex, partIndex - 1);
              }}
              onRemove={() => {
                onRemovePart(partIndex);
              }}
              onUpdate={(updates) => {
                onUpdatePart(partIndex, updates);
              }}
              part={part}
              structuralEditsRestricted={structuralEditsRestricted}
              totalParts={parts.length}
              useRandomVariables={question.useRandomVariables}
            />
          ))}
        </ol>
      </div>
    </div>
  );
}
